import { useEffect, useRef, useState } from 'react';
import { api, type ConversationDetail, type ConversationRow } from '../api.js';
import { CONCERN_FA, faInt, formatPrice } from '../labels.js';

/**
 * Screen 1b.
 *
 * The point of the layout: why this person is here sits above the transcript,
 * not buried in a third column an operator has to go looking for. By the time
 * they have read the first line they should know what they walked into.
 */

const POLL_MS = 8000;

const STATUS_FA: Record<string, string> = {
  active: 'با ربات',
  awaiting_operator: 'در انتظار',
  with_operator: 'با اپراتور',
  closed: 'بسته',
};

const HANDOFF_FA: Record<string, string> = {
  explicit_request: 'درخواست انسان',
  medical_sign: 'نشانه پزشکی',
  dissatisfaction: 'نارضایتی',
  low_confidence: 'اطمینان پایین',
  empty_results: 'نتیجه خالی',
  order_dispute: 'مشکل سفارش',
  model_unavailable: 'مدل در دسترس نبود',
};

/** Safety reasons wear red; everything else is a route, not an alarm. */
const HANDOFF_TONE: Record<string, string> = {
  medical_sign: 'bg-bad-bg text-bad',
  order_dispute: 'bg-warn-bg text-warn-text',
  empty_results: 'bg-warn-bg text-warn-text',
  model_unavailable: 'bg-warn-bg text-warn-text',
};

const GATE_FA: Record<string, string> = {
  medical_referral: 'پاسخ ربات متوقف شد — نشانه‌ی پزشکی',
  prescription_request: 'پاسخ ربات متوقف شد — داروی نسخه‌ای',
  llm_failed: 'مدل جواب نداد — به اپراتور رفت',
  llm_not_configured: 'مدل پیکربندی نشده بود',
};

const SKIN_FA: Record<string, string> = {
  oily: 'چرب',
  dry: 'خشک',
  combination: 'مختلط',
  normal: 'معمولی',
};

const SENSITIVITY_FA: Record<string, string> = {
  none: 'بدون حساسیت',
  mild: 'کمی حساس',
  high: 'پوست حساس',
};

/**
 * A stable tint per person.
 *
 * Two conversations open one after the other should not look like the same
 * person, and an operator learns a colour faster than a visitor number.
 */
const TINTS = [
  'bg-[#F3E7F7] text-[#8A3FA0]',
  'bg-[#E3F0F4] text-[#1C6C7E]',
  'bg-brand-100 text-brand',
  'bg-ok-bg text-ok-deep',
  'bg-warn-bg text-warn-text',
];

function tintFor(id: string): string {
  let sum = 0;
  for (let i = 0; i < id.length; i += 1) sum += id.charCodeAt(i);
  return TINTS[sum % TINTS.length]!;
}

function initials(name: string | null): string {
  if (!name) return '؟';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '؟';
  if (parts.length === 1) return parts[0]!.slice(0, 2);
  return `${parts[0]![0]}.${parts[1]![0]}`;
}

function nameOf(row: { displayName: string | null; visitorNumber: number }): string {
  return row.displayName ?? `بازدیدکننده ${faInt(row.visitorNumber)}`;
}

/** Elapsed time in the shortest form that is still unambiguous. */
function ago(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'همین حالا';
  if (mins < 60) return `${faInt(mins)} دقیقه`;
  if (mins < 1440) return `${faInt(Math.round(mins / 60))} ساعت`;
  return new Date(iso).toLocaleDateString('fa-IR', { day: 'numeric', month: 'long' });
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
}

const ONLINE_MS = 45_000;

function Tag({ text, tone }: { text: string; tone?: string }) {
  return (
    <span className={`badge ${tone ?? 'bg-paper-mute text-ink/55'}`}>{text}</span>
  );
}

function QueueRow({
  row,
  selected,
  onSelect,
}: {
  row: ConversationRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const online = Date.now() - new Date(row.lastSeenAt).getTime() < ONLINE_MS;
  const waiting = row.status === 'awaiting_operator';

  return (
    <button
      onClick={onSelect}
      className={`flex w-full gap-3 border-b border-ink/[.07] p-3.5 text-start transition ${
        selected ? 'border-e-[3px] border-e-brand bg-brand-row' : 'hover:bg-paper-app'
      }`}
    >
      <span
        className={`relative flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-xs font-bold ${tintFor(
          row.customerId,
        )}`}
      >
        {initials(row.displayName)}
        {online && (
          <span
            className={`absolute -bottom-px start-[-1px] h-2.5 w-2.5 rounded-full border-2 bg-ok ${
              selected ? 'border-brand-row' : 'border-white'
            }`}
          />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="truncate text-base font-bold">{nameOf(row)}</div>
          <div
            className={`shrink-0 text-xs font-medium ${
              waiting ? 'text-bad' : 'text-ink/40'
            }`}
          >
            {ago(row.waitingSince ?? row.lastMessageAt)}
          </div>
        </div>
        <div className="mt-1 line-clamp-1 text-sm text-ink/50">
          {row.lastMessage ?? '—'}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {row.handoffReason && (
            <Tag
              text={HANDOFF_FA[row.handoffReason] ?? row.handoffReason}
              tone={HANDOFF_TONE[row.handoffReason] ?? 'bg-brand-100 text-brand'}
            />
          )}
          {row.admittedDefeat && !row.handoffReason && (
            <Tag text="پیدا نشد" tone="bg-warn-bg text-warn-text" />
          )}
          {row.identityVerified && <Tag text="تأییدشده" tone="bg-ok-bg text-ok-deep" />}
        </div>
      </div>
    </button>
  );
}

function ProductCards({ products }: { products: Record<string, unknown>[] }) {
  return (
    <div className="mt-2 flex gap-2.5">
      {products.slice(0, 3).map((p, i) => (
        <div
          key={String(p['id'] ?? i)}
          className="flex flex-1 gap-2.5 rounded-xl border border-ink/[.09] bg-white p-2.5"
        >
          {typeof p['imageUrl'] === 'string' ? (
            <img
              src={p['imageUrl']}
              alt=""
              loading="lazy"
              className="h-[50px] w-[50px] shrink-0 rounded-md object-cover"
            />
          ) : (
            <div className="h-[50px] w-[50px] shrink-0 rounded-md bg-paper-mute" />
          )}
          <div className="min-w-0">
            <div className="line-clamp-2 text-sm font-medium leading-normal">
              {String(p['name'] ?? '')}
            </div>
            <div className="mt-1 text-xs font-bold">
              {formatPrice(p['price'] == null ? null : String(p['price']))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * @param focus a conversation to open straight away, from somewhere else in the
 * panel — the callback list has a button per row. Carries a timestamp as well
 * as an id so pressing the same row twice opens it twice; a bare id would look
 * unchanged to the effect below and the second press would do nothing.
 */
export function Conversations({
  inboxId,
  focus,
}: {
  inboxId?: string | undefined;
  focus?: { id: string; at: number } | undefined;
}) {
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [queue, setQueue] = useState<{ status: string; n: number }[]>([]);
  const [closedToday, setClosedToday] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /*
   * On a phone there is only room for one of the three columns, so opening a
   * conversation is a navigation rather than a selection: the inbox slides
   * away and a back arrow brings it back. On desktop both are on screen and
   * this flag does nothing.
   */
  const [onMobileDetail, setOnMobileDetail] = useState(false);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [statusFilter, setStatusFilter] = useState('awaiting_operator');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  async function loadList() {
    const params: Record<string, string> = {};
    if (inboxId) params['inboxId'] = inboxId;
    if (statusFilter) params['status'] = statusFilter;
    const res = await api.conversations(params);
    setRows(res.conversations);
    setQueue(res.queue);
    setClosedToday(res.closedToday);
  }

  async function loadDetail(id: string) {
    setDetail(await api.conversation(id));
  }


  /*
   * Opened from the callback list.
   *
   * The button there used to switch tabs and nothing else — the session id was
   * dropped on the way, so an operator with a phone number in front of them
   * landed on the queue and had to find the conversation by hand.
   *
   * The filter goes to "all" as well: a callback conversation stays `active`,
   * so under the default "waiting" filter the queue beside it would be empty
   * of the very row just opened.
   */
  useEffect(() => {
    if (!focus) return;
    setStatusFilter('');
    setSelectedId(focus.id);
    setOnMobileDetail(true);
    void loadDetail(focus.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.at]);
  useEffect(() => {
    void loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxId, statusFilter]);

  // Operators need new messages to appear without a manual refresh.
  useEffect(() => {
    const t = setInterval(() => {
      void loadList();
      if (selectedId) void loadDetail(selectedId);
    }, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, inboxId, statusFilter]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [detail?.messages.length]);

  async function send() {
    if (!selectedId || !reply.trim()) return;
    setSending(true);
    try {
      await api.replyToConversation(selectedId, reply.trim());
      setReply('');
      await loadDetail(selectedId);
      await loadList();
    } finally {
      setSending(false);
    }
  }

  async function setStatus(status: string) {
    if (!detail) return;
    await api.setConversationStatus(detail.conversation.id, status);
    await loadDetail(detail.conversation.id);
    await loadList();
  }

  const countOf = (s: string) => queue.find((q) => q.status === s)?.n ?? 0;
  const waiting = countOf('awaiting_operator');

  const FILTERS: [string, string][] = [
    ['awaiting_operator', 'در انتظار'],
    ['with_operator', 'با اپراتور'],
    ['active', 'با ربات'],
    ['', 'همه'],
  ];

  /*
   * The headline: the last thing the customer actually said.
   *
   * session.summary is the field this belongs in, but nothing writes it yet,
   * and a generated one-liner would be the model summarising a conversation it
   * already failed at. Their own words are true by construction.
   */
  const lastAsked = [...(detail?.messages ?? [])]
    .reverse()
    .find((m) => m.role === 'user');

  const openHandoff = detail?.handoffs.find((h) => h.status === 'queued');

  return (
    <div className="flex min-h-0 flex-1 bg-white">
      {/* queue */}
      <div
        className={`w-full shrink-0 flex-col border-e border-ink/[.08] md:flex md:w-[308px] ${
          onMobileDetail ? 'hidden' : 'flex'
        }`}
      >
        <div className="border-b border-ink/[.08] px-4 pb-3.5 pt-4 md:px-5">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-md font-bold">گفتگوها</h2>
            <span className="text-xs text-ink/45">
              {faInt(waiting)} در انتظار · {faInt(closedToday)} بسته امروز
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {FILTERS.map(([value, label]) => (
              <button
                key={value || 'all'}
                onClick={() => setStatusFilter(value)}
                className={statusFilter === value ? 'chip-on' : 'chip'}
              >
                {label}
                {value && countOf(value) > 0 && ` ${faInt(countOf(value))}`}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 && (
            <p className="p-8 text-center text-sm text-ink/40">
              {statusFilter === 'awaiting_operator'
                ? 'کسی در صف نیست.'
                : 'گفتگویی نیست.'}
            </p>
          )}
          {rows.map((c) => (
            <QueueRow
              key={c.id}
              row={c}
              selected={selectedId === c.id}
              onSelect={() => {
                setSelectedId(c.id);
                setOnMobileDetail(true);
                void loadDetail(c.id);
              }}
            />
          ))}
        </div>
      </div>

      {/* transcript */}
      <div
        className={`min-w-0 flex-1 flex-col border-e border-ink/[.08] md:flex ${
          onMobileDetail ? 'flex' : 'hidden'
        }`}
      >
        {!detail && (
          <p className="hidden p-10 text-center text-sm text-ink/40 md:block">
            یک گفتگو را انتخاب کن
          </p>
        )}

        {detail && (
          <>
            <div className="border-b border-ink/[.08] bg-white px-4 py-3.5 md:px-6 md:py-4">
              <div className="flex items-start gap-3">
                <button
                  onClick={() => setOnMobileDetail(false)}
                  aria-label="بازگشت به صندوق"
                  className="-ms-1 shrink-0 py-1 text-lg text-ink md:hidden"
                >
                  ←
                </button>
                <div className="min-w-0 flex-1">
                  {openHandoff ? (
                    <div className="flex items-center gap-2">
                      <span className="h-[7px] w-[7px] rounded-full bg-bad" />
                      <span className="text-xs font-medium text-bad">
                        چرا به شما رسید — {HANDOFF_FA[openHandoff.reason] ?? openHandoff.reason}
                        {detail.conversation.status === 'awaiting_operator' &&
                          rows.find((r) => r.id === detail.conversation.id)?.waitingSince &&
                          ` · ${ago(
                            rows.find((r) => r.id === detail.conversation.id)!.waitingSince!,
                          )} در انتظار`}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="h-[7px] w-[7px] rounded-full bg-ok" />
                      <span className="text-xs font-medium text-ink/50">
                        {STATUS_FA[detail.conversation.status] ?? detail.conversation.status}
                      </span>
                    </div>
                  )}

                  <h1 className="mt-2 line-clamp-2 text-lg font-bold tracking-tight">
                    {lastAsked?.content ?? '—'}
                  </h1>

                  <div className="mt-2.5 flex flex-wrap items-center gap-2.5 text-sm text-ink/55">
                    <span
                      className={`flex h-[26px] w-[26px] items-center justify-center rounded-full text-2xs font-bold ${tintFor(
                        detail.customer.id,
                      )}`}
                    >
                      {initials(detail.customer.displayName)}
                    </span>
                    <span className="text-base font-bold text-ink">
                      {nameOf(detail.customer)}
                    </span>
                    {detail.customer.identityVerified ? (
                      <span className="badge rounded-full bg-ok-bg px-2.5 text-ok-deep">
                        هویت تأییدشده
                      </span>
                    ) : (
                      /* Says so plainly. An operator reading a name off this
                         panel needs to know whether WordPress vouched for it,
                         or the visitor typed it into their own browser. */
                      <span className="badge rounded-full bg-paper-mute px-2.5 text-ink/50">
                        تأیید نشده
                      </span>
                    )}
                    {detail.orders.length > 0 && (
                      <>
                        <span className="text-ink/20">·</span>
                        <span>{faInt(detail.orders.length)} سفارش قبلی</span>
                      </>
                    )}
                    {detail.skinProfile?.isPregnantOrNursing && (
                      <>
                        <span className="text-ink/20">·</span>
                        <span className="font-medium text-bad">باردار / شیرده</span>
                      </>
                    )}
                    {detail.currentPage && (
                      <>
                        <span className="text-ink/20">·</span>
                        <a
                          href={detail.currentPage.url}
                          target="_blank"
                          rel="noreferrer"
                          className="line-clamp-1 max-w-[38ch] font-medium text-brand underline decoration-brand/35 underline-offset-[3px]"
                        >
                          {detail.currentPage.productName ??
                            detail.currentPage.title ??
                            detail.currentPage.url}{' '}
                          ↗
                        </a>
                      </>
                    )}
                  </div>
                </div>

                {/*
                  What each one does, said on the button rather than learned by
                  pressing it. Taking a conversation is the only thing that
                  silences the assistant before you have typed anything —
                  sending a reply does it too, which is why the button
                  disappears once it is yours.
                */}
                <div className="hidden shrink-0 items-center gap-2 md:flex">
                  {detail.conversation.status === 'with_operator' && (
                    <span className="badge bg-ok-bg text-ok-deep">دست توست · ربات ساکت</span>
                  )}
                  {detail.conversation.status === 'with_operator' && (
                    <button
                      className="btn-ghost px-3.5 py-2.5 text-sm"
                      title="ربات دوباره در این گفتگو جواب می‌دهد. گفتگو بسته نمی‌شود."
                      onClick={() => void setStatus('active')}
                    >
                      برگرداندن به ربات
                    </button>
                  )}
                  {detail.conversation.status !== 'with_operator' &&
                    detail.conversation.status !== 'closed' && (
                      <button
                        className="btn-primary px-4 py-2.5 text-sm"
                        title="ربات دیگر در این گفتگو جواب نمی‌دهد و برای بقیه «برداشته‌شده» علامت می‌خورد. فرستادن پاسخ هم همین کار را می‌کند."
                        onClick={() => void setStatus('with_operator')}
                      >
                        من جواب می‌دهم
                      </button>
                    )}
                  {detail.conversation.status !== 'closed' ? (
                    <button
                      className="btn-ghost px-3.5 py-2.5 text-sm"
                      title="گفتگو تمام می‌شود و از صف بیرون می‌رود. اگر دوباره پیام بدهد، باز می‌شود."
                      onClick={() => void setStatus('closed')}
                    >
                      بستن گفتگو
                    </button>
                  ) : (
                    <button
                      className="btn-ghost px-3.5 py-2.5 text-sm"
                      title="گفتگو به ربات برمی‌گردد."
                      onClick={() => void setStatus('active')}
                    >
                      باز کردن دوباره
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2 border-b border-ink/[.08] bg-white px-4 pb-3.5 md:hidden">
              {detail.conversation.status !== 'with_operator' &&
                detail.conversation.status !== 'closed' && (
                  <button
                    className="btn-primary flex-1 py-2 text-sm"
                    onClick={() => void setStatus('with_operator')}
                  >
                    من جواب می‌دهم
                  </button>
                )}
              {detail.conversation.status !== 'closed' ? (
                <button
                  className="btn-ghost flex-1 py-2 text-sm"
                  onClick={() => void setStatus('closed')}
                >
                  بستن گفتگو
                </button>
              ) : (
                <button
                  className="btn-ghost flex-1 py-2 text-sm"
                  onClick={() => void setStatus('active')}
                >
                  باز کردن دوباره
                </button>
              )}
            </div>

            <div
              ref={logRef}
              className="flex flex-1 flex-col gap-4 overflow-y-auto bg-paper-chat px-4 py-4 md:px-6 md:py-6"
            >
              {detail.messages.map((m) => {
                const gate = typeof m.meta?.['gate'] === 'string' ? m.meta['gate'] : null;
                const stopped = gate ? GATE_FA[gate] : undefined;
                const products = Array.isArray(m.meta?.['products'])
                  ? (m.meta['products'] as Record<string, unknown>[])
                  : [];

                if (m.role === 'user') {
                  return (
                    <div key={m.id} className="max-w-[70%] self-start">
                      <div className="rounded-2xl rounded-es-sm bg-paper-mute px-4 py-3 text-base">
                        <span className="whitespace-pre-wrap">{m.content}</span>
                      </div>
                      <div className="mt-1.5 text-2xs text-ink/35">
                        {nameOf(detail.customer)} · {clock(m.createdAt)}
                      </div>
                    </div>
                  );
                }

                if (m.role === 'system') return null;

                return (
                  <div key={m.id} className="max-w-[78%] self-end">
                    <div
                      className={`rounded-2xl rounded-ee-sm px-4 py-3 text-base ${
                        m.role === 'operator'
                          ? 'bg-brand text-white'
                          : 'border border-ink/[.09] bg-white'
                      }`}
                    >
                      <span className="whitespace-pre-wrap">{m.content}</span>
                    </div>

                    {products.length > 0 && <ProductCards products={products} />}

                    <div className="mt-1.5 flex items-center gap-2 text-2xs text-ink/35">
                      <span className="rounded-sm bg-paper-mute px-1.5 py-0.5 font-medium text-ink/50">
                        {m.role === 'operator' ? 'اپراتور' : 'ربات'}
                      </span>
                      {clock(m.createdAt)}
                      {typeof m.meta?.['ms'] === 'number' &&
                        ` · ${faInt(Math.round(Number(m.meta['ms']) / 100) / 10)} ثانیه`}
                      {products.length > 0 &&
                        ` · ${faInt(products.length)} محصول فرستاد`}
                    </div>

                    {stopped && (
                      <div className="mt-2 flex items-center gap-3 rounded-xl border border-bad-line bg-bad-bg px-4 py-3">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bad text-2xs font-bold text-white">
                          !
                        </span>
                        <div className="text-sm font-bold text-bad-deep">{stopped}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="border-t border-ink/[.08] bg-white px-4 pb-4 pt-3.5 md:px-6">
              {/* No "private note" or "saved replies" tabs: neither exists yet,
                  and a tab that does nothing is worse than a missing one. */}
              <div className="flex items-end gap-3 rounded-xl border border-brand-300 bg-brand-50 px-4 py-3">
                <input
                  className="flex-1 bg-transparent text-base outline-none placeholder:text-ink/35"
                  placeholder={`پاسخ به ${nameOf(detail.customer)}…`}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void send()}
                />
                <button
                  className="btn-send h-[34px] w-[34px] shrink-0 !px-0 text-base md:h-auto md:w-auto md:!px-5 md:py-2.5 md:text-sm"
                  disabled={!reply.trim() || sending}
                  onClick={() => void send()}
                >
                  <span className="md:hidden">↑</span>
                  <span className="hidden md:inline">فرستادن</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* context — the expensive column, and the reason we built our own (D-016) */}
      {/* The context column is desktop-only. Its contents are not dropped on a
          phone — the profile, the orders and the current page fold up into the
          conversation header above, which is where they matter most. */}
      <div className="hidden w-[318px] shrink-0 flex-col overflow-y-auto bg-paper-chat lg:flex">
        {!detail && (
          <p className="p-6 text-sm text-ink/40">زمینه‌ی گفتگو اینجا نمایش داده می‌شود</p>
        )}

        {detail && (
          <>
            <section className="border-b border-ink/[.08] px-5 py-4">
              <div className="field-label">پروفایل پوستی</div>
              {detail.skinProfile ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {detail.skinProfile.isPregnantOrNursing && (
                    <span className="rounded px-2.5 py-1 text-xs font-bold bg-bad-bg text-bad-deep">
                      باردار / شیرده
                    </span>
                  )}
                  {detail.skinProfile.skinType && (
                    <span className="rounded bg-paper-mute px-2.5 py-1 text-xs text-ink/70">
                      پوست {SKIN_FA[detail.skinProfile.skinType] ?? detail.skinProfile.skinType}
                    </span>
                  )}
                  {detail.skinProfile.sensitivity && (
                    <span className="rounded bg-paper-mute px-2.5 py-1 text-xs text-ink/70">
                      {SENSITIVITY_FA[detail.skinProfile.sensitivity] ??
                        detail.skinProfile.sensitivity}
                    </span>
                  )}
                  {detail.skinProfile.concerns?.map((c) => (
                    <span key={c} className="rounded bg-paper-mute px-2.5 py-1 text-xs text-ink/70">
                      {CONCERN_FA[c] ?? c}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-sm text-ink/40">هنوز پرسش‌نامه پر نشده</p>
              )}
            </section>

            <section className="border-b border-ink/[.08] px-5 py-4">
              <div className="flex items-baseline justify-between">
                <div className="field-label">سفارش‌های قبلی</div>
                {detail.orders.length > 0 && (
                  <div className="text-xs text-ink/35">
                    {faInt(detail.orders.length)} از ووکامرس
                  </div>
                )}
              </div>
              {detail.orders.length > 0 ? (
                <div className="mt-3 flex flex-col gap-3">
                  {detail.orders.slice(0, 3).map((o) => (
                    <div key={o.id} className="border-t border-ink/[.07] pt-3 first:border-t-0 first:pt-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium" dir="ltr">
                          #{o.number}
                        </span>
                        <span className="badge bg-ok-bg text-ok-deep">
                          {o.statusFa ?? o.status}
                        </span>
                      </div>
                      <div className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-ink/55">
                        {o.items?.map((i) => i.name).join('، ')}
                      </div>
                    </div>
                  ))}
                </div>
              ) : detail.customer.identityVerified ? (
                <p className="mt-3 text-sm text-ink/40">سفارشی ثبت نشده</p>
              ) : (
                <p className="mt-3 text-sm leading-relaxed text-ink/40">
                  تا وقتی هویت تأیید نشده، تاریخچه نشان داده نمی‌شود
                </p>
              )}
            </section>

            <section className="border-b border-ink/[.08] px-5 py-4">
              <div className="field-label">مشخصات</div>
              <div className="mt-3 flex flex-col gap-2.5 text-sm">
                <div className="flex justify-between gap-2.5">
                  <span className="text-ink/45">ایمیل</span>
                  <span className="truncate font-mono text-xs text-brand" dir="ltr">
                    {detail.customer.email ?? '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-2.5">
                  <span className="text-ink/45">موبایل</span>
                  <span className="font-mono text-xs" dir="ltr">
                    {detail.customer.phone ?? '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-2.5">
                  <span className="text-ink/45">اولین بازدید</span>
                  <span>
                    {new Date(detail.customer.createdAt).toLocaleDateString('fa-IR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </section>

            <section className="px-5 py-4">
              <div className="flex items-baseline justify-between">
                <div className="field-label">مسیر امروز</div>
                <div className="text-xs font-medium text-brand">
                  {faInt(detail.pages.length)} صفحه
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2.5">
                {detail.pages.length === 0 && (
                  <p className="text-sm text-ink/40">صفحه‌ای ثبت نشده</p>
                )}
                {detail.pages.slice(0, 8).map((p, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-2.5">
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className={`line-clamp-2 text-sm leading-relaxed ${
                        p.productName
                          ? 'font-medium text-brand underline decoration-brand/30 underline-offset-[3px]'
                          : 'text-ink/60'
                      }`}
                    >
                      {p.productName ?? p.title ?? p.url}
                    </a>
                    <span className="shrink-0 text-2xs text-ink/40">
                      {clock(p.viewedAt)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
